// App.jsx - 主应用组件
import React, { useState, useEffect } from 'react';
import './App.css';
import CategoryNav from './components/CategoryNav';
import MarketList from './components/MarketList';
import MarketDetail from './components/MarketDetail';
import MarketCreate from './components/MarketCreate';
import ProfilePage from './components/ProfilePage';
import MintTestPage from './components/MintTestPage';

import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Routes, Route, Link, NavLink, useNavigate } from 'react-router-dom';
import { parseUnits, ethers } from 'ethers';
import { AroundUIHelperContract } from './contracts/AroundUIHelper';
import { AroundMarketContract } from './contracts/AroundMarket';
import { useEthers } from './context/EthersContext';
import { getAddresses } from './utils/address';
import { formatCurrencyCompact as formatCurrency } from './utils/numberFormat';
import Toast from './components/common/Toast';
import { formatErrorMessage } from './utils/errors';

const App = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showBetModal, setShowBetModal] = useState(false);
  const [betSide, setBetSide] = useState(null);
  const [betAmount, setBetAmount] = useState('');
  const [betMarket, setBetMarket] = useState(null);
  const [betMessage, setBetMessage] = useState('');
  const [betLoading, setBetLoading] = useState(false);
  const [txToast, setTxToast] = useState(null);
  const [estWinnings, setEstWinnings] = useState(0);
  const navigate = useNavigate();

  const handleBackToHome = () => {
    navigate('/');
  };

  // 处理下注按钮点击
  const handleBetClick = (market, side) => {
    setBetMarket(market);
    setBetSide(side);
    setBetAmount('');
    setBetMessage('');
    setShowBetModal(true);
  };

  const { signer: ctxSigner, provider: ctxProvider } = useEthers();

  const confirmBet = async () => {
    try {
      setBetLoading(true);
      setBetMessage('');
      const signer = ctxSigner;
      if (!signer) throw new Error('Please connect wallet');
      const provider = ctxProvider || signer?.provider;
      const { AroundUIHelper, AroundMarket } = getAddresses();
      if (!betMarket?.id) throw new Error('Missing market id');
      const ui = new AroundUIHelperContract(AroundUIHelper, provider);
      const idBn = ethers.toBigInt(betMarket.id);

      // Call indexMarketData - returns named tuple
      const result = await ui.indexMarketData(idBn);

      // Debug: Log full data structure
      console.log('===== MARKET DATA DEBUG =====');
      console.log('Raw indexMarketData result:', result);
      console.log('Result type:', typeof result);
      console.log('Is array:', Array.isArray(result));

      // Handle different return formats from ethers.js
      // Named tuple from Solidity: (thisPoolInfo, thisFeeInfo, thisMarketInfo, thisLiqudityInfo, thisOracleInfo, thisYesPrice, thisNoPrice)
      let data;
      if (Array.isArray(result)) {
        // If returned as array, map to object
        data = {
          thisPoolInfo: result[0],
          thisFeeInfo: result[1],
          thisMarketInfo: result[2],
          thisLiqudityInfo: result[3],
          thisOracleInfo: result[4],
          thisYesPrice: result[5],
          thisNoPrice: result[6]
        };
      } else {
        // Already an object
        data = result;
      }

      console.log('Processed data:', data);
      console.log('thisMarketInfo:', data?.thisMarketInfo);
      console.log('Passed market prop:', betMarket);

      let col = data?.thisPoolInfo?.collateral || data?.thisMarketInfo?.collateral;
      const ZERO = '0x0000000000000000000000000000000000000000';
      if (!col || col === ZERO) {
        const fallback = getAddresses()?.USDC;
        col = fallback;
      }
      const d = await ui.getDecimals(col);
      const decimals = Number(d) || 6;
      if (!col || col === ZERO) throw new Error('Collateral token not set');
      let amountRaw = parseUnits(String(betAmount || '0'), decimals);
      if (amountRaw <= 0n) throw new Error('Enter a positive amount');

      // Extract market info
      let marketInfo = data?.thisMarketInfo || {};

      // Handle if marketInfo is also an array
      // MarketInfo struct order: result, ifOpenAave, ifUpdateFinallyAmount, participants, startTime, endTime, collateral, totalRaffleTicket
      if (Array.isArray(marketInfo)) {
        console.log('thisMarketInfo is array, converting to object...');
        marketInfo = {
          result: marketInfo[0],
          ifOpenAave: marketInfo[1],
          ifUpdateFinallyAmount: marketInfo[2],
          participants: marketInfo[3],
          startTime: marketInfo[4],
          endTime: marketInfo[5],
          collateral: marketInfo[6],
          totalRaffleTicket: marketInfo[7]
        };
        console.log('Converted marketInfo from array:', marketInfo);
      }

      console.log('Final marketInfo:', marketInfo);
      console.log('marketInfo.result type:', typeof marketInfo?.result, 'value:', marketInfo?.result);
      console.log('marketInfo.endTime type:', typeof marketInfo?.endTime, 'value:', marketInfo?.endTime);

      const resultFlag = Number(marketInfo?.result ?? 0);
      const endTime = Number(marketInfo?.endTime ?? 0);
      const nowSec = Math.floor(Date.now() / 1000);

      console.log('Market status check:', {
        id: betMarket.id,
        resultFlag,
        resultRaw: marketInfo?.result,
        endTime,
        endTimeRaw: marketInfo?.endTime,
        endDate: new Date(endTime * 1000).toISOString(),
        nowSec,
        nowDate: new Date(nowSec * 1000).toISOString(),
        timeRemaining: endTime - nowSec,
        isResolved: resultFlag !== 0,
        isExpired: endTime <= nowSec,
        canTrade: resultFlag === 0 && endTime > nowSec
      });

      // Strict market validation - prevent trading on expired or resolved markets
      if (resultFlag !== 0) {
        const resultMap = { 0: 'Pending', 1: 'Yes', 2: 'No', 3: 'Invalid' };
        throw new Error(`Market is already resolved (Result: ${resultMap[resultFlag] || resultFlag}). Trading is closed.`);
      }

      if (endTime <= nowSec) {
        const remainingSec = nowSec - endTime;
        const remainingMins = Math.floor(remainingSec / 60);
        const remainingHours = Math.floor(remainingMins / 60);
        const remainingDays = Math.floor(remainingHours / 24);
        throw new Error(`Market has ended ${remainingDays > 0 ? `${remainingDays} days` : remainingHours > 0 ? `${remainingHours} hours` : `${remainingMins} minutes`} ago. Trading is closed.`);
      }

      const approveAmount = parseUnits(String(betAmount || '0'), decimals);

      // Balance check
      const addr = await signer.getAddress();
      const bal = await ui.getTokenBalance(col, addr);
      if (approveAmount > (bal ?? 0n)) throw new Error('Insufficient collateral balance');
      const erc20 = new ethers.Contract(col, [
        "function approve(address spender,uint256 amount) returns (bool)",
        "function allowance(address owner,address spender) view returns (uint256)"
      ], signer);
      const current = await erc20.allowance(addr, AroundMarket);
      if (ethers.toBigInt(current || 0) < approveAmount) {
        const approveTx = await erc20.approve(AroundMarket, approveAmount);
        await approveTx.wait();
      }
      const am = new AroundMarketContract(AroundMarket, signer);
      const bet = betSide === 'yes' ? 1 : 2;
      // Try sending, adapt amount if estimation fails
      const trySend = async (amt) => {
        return await am.buyShares(bet, amt, idBn);
      };
      let attempt = 0;
      while (attempt < 3) {
        try {
          const tx = await trySend(amountRaw);
          console.log('Buy sent', { hash: tx?.hash, amountRaw: amountRaw.toString(), bet, marketId: betMarket.id });
          await tx.wait();
          console.log('Buy confirmed');
          setBetMessage('Purchase completed');
          setShowBetModal(false);
          setTxToast({ type: 'success', title: 'Trade Successful', message: 'Buy completed', txHash: tx?.hash || null, chainId: getAddresses().chainId });
          setBetLoading(false);
          return;
        } catch (err) {
          console.log('Buy failed', { attempt, amountRaw: amountRaw.toString(), error: err?.message });
          attempt++;
          amountRaw = amountRaw / 2n;
          if (amountRaw <= 0n) break;
        }
      }
      throw new Error('Trade failed. Try a smaller amount or check market status');
    } catch (e) {
      console.error(e);
      setBetMessage(formatErrorMessage(e, 'Transaction failed'));
    } finally {
      setBetLoading(false);
    }
  };

  useEffect(() => {
    const calcEstimate = async () => {
      try {
        setEstWinnings(0);
        if (!betMarket?.id || !betSide) return;
        console.log('[Estimate] init', { marketId: betMarket.id, side: betSide, amount: betAmount });
        const provider = ctxProvider;
        const { AroundUIHelper } = getAddresses();
        if (!provider || !AroundUIHelper) return;
        const ui = new AroundUIHelperContract(AroundUIHelper, provider);
        const idBn = ethers.toBigInt(betMarket.id);
        const data = await ui.indexMarketData(idBn);
        let col = data?.thisPoolInfo?.collateral || data?.thisMarketInfo?.collateral || '';
        const ZERO = '0x0000000000000000000000000000000000000000';
        if (!col || col === ZERO) col = getAddresses()?.USDC || '';
        const d = await ui.getDecimals(col);
        const decimals = Number(d) || 6;
        console.log('[Estimate] decimals', { token: col, decimals });
        const amt = parseUnits(String(betAmount || 0), decimals);
        console.log('[Estimate] amountIn', { raw: betAmount, amountIn: amt?.toString?.() });
        if (amt <= 0n) { setEstWinnings(0); return; }
        const bet = betSide === 'yes' ? 1 : 2;
        console.log('[Estimate] calling estimateWinAmount', { bet, marketId: idBn?.toString?.() });
        console.log('%c🚀[amt]-159:', 'color: #e04c7e', amt);
        const win = await ui.estimateWinAmount(bet, amt, idBn).catch(() => 0n);
        console.log('%c🚀[win]-160:', 'color: #a4153b', win);
        const val = Number(ethers.formatUnits(win || 0n, decimals));
        console.log('[Estimate] result', { winBn: win?.toString?.(), formatted: val });
        setEstWinnings(val);
      } catch (e) {
        console.warn('[Estimate] failed:', e);
        setEstWinnings(0);
      }
    };
    calcEstimate();
  }, [betAmount, betSide, betMarket, ctxProvider]);
  // 模拟初始数据
  const categories = [
    { id: 'all', name: 'All Markets' },
    { id: '1', name: 'Crypto' },
    { id: '2', name: 'Sport' },
    { id: '3', name: 'Game' },
    { id: '4', name: 'Politics' },
    { id: '5', name: 'Economy' },
    { id: '6', name: 'Tech' },
    { id: '7', name: 'Weather' },
    { id: '0', name: 'Other' }
  ];

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <Link to="/" className="logo-container">
            <img src="/alogo.jpg" alt="AroundMarket Logo" style={{ height: 28 }} />
            <h1 className="logo">AroundMarket</h1>
            <span className="beta-tag">Beta</span>
          </Link>
          <nav className="app-navbar">
            <div className="nav-links">
              <NavLink to="/create" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>CreateMarket</NavLink>
              <a href="https://arbitral.vercel.app" target="_blank" rel="noreferrer" className="nav-link">Arbitrament</a>
              <NavLink to="/mint" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>Faucet</NavLink>
              <a href="https://around-market-docs.vercel.app/" target="_blank" rel="noreferrer" className="nav-link">Docs</a>
              <NavLink to="/profile" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>Profile</NavLink>
            </div>
            <div className="nav-actions">
              <ConnectButton />
            </div>
          </nav>
        </div>
      </header>

      <Routes>
        <Route
          path="/"
          element={(
            <>
              <CategoryNav
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
              />
              <main className="main-content">
                <MarketList category={selectedCategory} onBetClick={handleBetClick} />
              </main>
            </>
          )}
        />
        <Route path="/profile" element={<main className="main-content"><ProfilePage signerOverride={ctxSigner} onExploreMarkets={() => navigate('/')} /></main>} />
        <Route path="/create" element={<main className="main-content"><MarketCreate signerOverride={ctxSigner} /></main>} />
        <Route path="/market/:id" element={<MarketDetail signerOverride={ctxSigner} onBack={handleBackToHome} onBetClick={handleBetClick} />} />
        <Route path="/mint" element={<main className="main-content"><MintTestPage signerOverride={ctxSigner} /></main>} />
      </Routes>

      {/* 下注模态框 */}
      {showBetModal && (
        <div className="modal-overlay">
          <div className="bet-modal">
            <div className="modal-header">
              <h3>Place Your Bet</h3>
              <button className="close-btn" onClick={() => setShowBetModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <div className="bet-side-indicator">
                You are betting on: <span className={`side-tag ${betSide}`}>{betSide === 'yes' ? 'YES' : 'NO'}</span>
              </div>
              <div className="input-group">
                <label htmlFor="amount">Amount</label>
                <input
                  type="number"
                  id="amount"
                  placeholder="Enter amount"
                  min="1"
                  step="0.01"
                  value={betAmount}
                  onChange={(e) => setBetAmount(e.target.value)}
                />
              </div>
              <div className="input-group">
                <label htmlFor="estimated">Estimated Winnings</label>
                <div className="amount-value">{formatCurrency(estWinnings || 0)}</div>
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setShowBetModal(false)} disabled={betLoading}>Cancel</button>
                <button className="confirm-btn" onClick={confirmBet} disabled={betLoading}>{betLoading ? 'Confirming…' : 'Confirm Bet'}</button>
              </div>
              {betMessage && <div className="message">{betMessage}</div>}
            </div>
          </div>
        </div>
      )}
      <Toast
        open={!!txToast}
        type={txToast?.type}
        title={txToast?.title}
        message={txToast?.message}
        txHash={txToast?.txHash}
        chainId={txToast?.chainId}
        onClose={() => setTxToast(null)}
      />

      <footer className="app-footer">
        <p>AroundMarket © 2025 - Prediction Market Platform. All rights reserved.</p>
        <div className="footer-links">
          <a href="/">Terms</a>
          <a href="/">Privacy</a>
          <a href="/">FAQ</a>
          <a href="/">Docs</a>
        </div>
      </footer>
    </div>
  );
};

export default App;
